import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@atoms/index"

import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import CheckboxCard from "@molecules/CheckboxCard/CheckboxCard"
import Dialog from "@molecules/Dialog/Dialog"

import styles from "./EstimateQuickOrderDialog.module.scss"
import { EstimateQuickOrderDialogProps } from "./EstimateQuickOrderDialog.types"

export default function EstimateQuickOrderDialog(props: EstimateQuickOrderDialogProps) {
    const { isOpen, onOpenChange, lineItems, onCreateOrder } = props

    const freeLineItemIDs = lineItems.filter((lineItem) => !lineItem.purchase_order).map((lineItem) => lineItem.id)
    const allOrdered = freeLineItemIDs.length === 0

    const [selectedLineItemIDs, setSelectedLineItemIDs] = useState(freeLineItemIDs)
    const [hasSelectionError, setHasSelectionError] = useState(false)

    // Store onOpenChange in a ref so the callback identity stays stable
    // across renders. The Popover's useEffect depends on onOpenChange,
    // and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((isNowOpen: boolean) => {
        onOpenChangeRef.current(isNowOpen)
    }, [])

    // Re-sync the selection each time the dialog opens so lines ordered (or
    // freed) since the last open are reflected.
    useEffect(() => {
        if (isOpen) {
            setSelectedLineItemIDs(freeLineItemIDs)
            setHasSelectionError(false)
        }
    }, [isOpen])

    const handleToggle = (lineItemID: number) => {
        setSelectedLineItemIDs((prevSelected) =>
            prevSelected.includes(lineItemID)
                ? prevSelected.filter((selectedID) => selectedID !== lineItemID)
                : [...prevSelected, lineItemID],
        )
    }

    const handleCreateOrder = () => {
        if (selectedLineItemIDs.length === 0) {
            setHasSelectionError(true)
        } else {
            onCreateOrder(selectedLineItemIDs)
        }
    }

    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <Dialog.Header.Root>
                    <Dialog.Header.Title title="Create Purchase Order" />
                    <Dialog.Header.CloseButton />
                </Dialog.Header.Root>
                <Dialog.Body>
                    <div className={styles.body}>
                        {allOrdered ? (
                            <AlertBanner
                                type="card"
                                status="info"
                                message="All of this estimate's part line items are already on a purchase order."
                            />
                        ) : (
                            <p>Select which line items you&apos;d like to order:</p>
                        )}
                        {lineItems.map((lineItem) => {
                            const orderedOn = lineItem.purchase_order

                            return (
                                <CheckboxCard
                                    key={lineItem.id}
                                    checked={selectedLineItemIDs.includes(lineItem.id)}
                                    onCheckedChange={() => handleToggle(lineItem.id)}
                                    label={`${lineItem.description} (Qty: ${parseFloat(lineItem.quantity)})`}
                                    showLabel={true}
                                    supportText={orderedOn ? `On ${orderedOn.label}` : ""}
                                    showSupportText={!!orderedOn}
                                    isControlled={false}
                                    isDisabled={!!orderedOn}
                                />
                            )
                        })}
                        {hasSelectionError && (
                            <span className={styles.errorMessage}>Please select at least one line item to order.</span>
                        )}
                    </div>
                </Dialog.Body>
                <Dialog.Footer.Root>
                    <Button
                        variant="solid"
                        size="lg"
                        onClick={handleCreateOrder}
                        isFullWidth={true}
                        isDisabled={allOrdered}
                    >
                        Create Order
                    </Button>
                </Dialog.Footer.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}
